import axios from '@/plugins/axios'
import type {
  EarnedDate,
  Employee,
  EmployeeWorkLocation,
  NewTimeEntry,
  Notice,
  PayCode,
  PayPeriod,
  PayPeriodCard,
  TimeEntry,
} from '@/types'
import { toIsoDate } from '@/utils/dates'

/**
 * Every call the UI makes to the XGuard API, in one place. Paths are relative
 * to the versioned base URL (`/api/v1`). Dates in routes go as `YYYY-MM-DD`.
 */
const dataService = {
  // ── Reference data ────────────────────────────────────
  async getPayPeriod(): Promise<PayPeriod[]> {
    const { data } = await axios.get<PayPeriod[]>('/TimeEntry/GetPayPeriod')
    return data ?? []
  },

  async getPayCodes(): Promise<PayCode[]> {
    const { data } = await axios.get<PayCode[]>('/TimeEntry/GetPayCodes')
    return data ?? []
  },

  async getDropDownDates(): Promise<EarnedDate[]> {
    const { data } = await axios.get<EarnedDate[]>('/TimeEntry/GetDropDownDates')
    return data ?? []
  },

  async getNotices(): Promise<Notice[]> {
    const { data } = await axios.get<Notice[]>('/Notifications/GetNotices')
    return data ?? []
  },

  async getCallOutMessage(): Promise<Notice[]> {
    const { data } = await axios.get<Notice[]>('/Notifications/GetCallOutMessage')
    return data ?? []
  },

  // ── Employee ──────────────────────────────────────────
  async getEmployeeByEmployeeNumber(employeeNumber: string): Promise<Employee[]> {
    const { data } = await axios.get<Employee[]>(`/TimeEntry/GetEmployeeByEmployeeNumber/${encodeURIComponent(employeeNumber)}`)
    return data ?? []
  },

  async getEmployeeWorkLocation(employeeNumber: string): Promise<EmployeeWorkLocation[]> {
    const { data } = await axios.get<EmployeeWorkLocation[]>(`/TimeEntry/GetEmployeeWorkLocationByEmployeeNumber/${encodeURIComponent(employeeNumber)}`)
    return data ?? []
  },

  // ── Time cards ────────────────────────────────────────
  /** Number of existing cards that overlap the window; 0 means the card may be saved. */
  async verifyTimeCard(employeeNumber: string, dateEarned: string | Date, timeStart: string, timeEnd: string): Promise<number> {
    const { data } = await axios.get<number>(
      `/TimeEntry/VerifyTimeCard/${encodeURIComponent(employeeNumber)}/${toIsoDate(dateEarned)}/${timeStart}/${timeEnd}`,
    )
    return data ?? 0
  },

  /**
   * Every entry in the pay period. The stored procedure behind this also
   * returns one placeholder row per day that has no entries (a negative id,
   * the date, and nothing else), which the old grid displayed as blank rows.
   * They are not entries, so they are dropped here.
   */
  async getTimeCardsByPayPeriod(payPeriodDate: string, employeeNumber: string): Promise<TimeEntry[]> {
    const { data } = await axios.get<TimeEntry[]>(
      `/TimeEntry/GetTimeCardsByPayPeriodDateV2/${toIsoDate(payPeriodDate)}/${encodeURIComponent(employeeNumber)}`,
    )
    return (data ?? []).filter((row) => row.id > 0)
  },

  async getTimeCardsByDateEarned(dateEarned: string | Date, employeeNumber: string): Promise<TimeEntry[]> {
    const { data } = await axios.get<TimeEntry[]>(
      `/TimeEntry/GetTimeCardsByDateEarned/${toIsoDate(dateEarned)}/${encodeURIComponent(employeeNumber)}`,
    )
    return data ?? []
  },

  async addTimeCard(card: NewTimeEntry): Promise<boolean> {
    const { data } = await axios.post<boolean>('/TimeEntry/AddTimeCard', card)
    return data === true
  },

  async deleteTimeCard(card: TimeEntry): Promise<boolean> {
    const { data } = await axios.delete<boolean>('/TimeEntry/DeleteTimeCard', { data: card })
    return data === true
  },

  // ── Pay period confirmation ───────────────────────────
  async getPayPeriodCardByKey(payPeriodDate: string, employeeNumber: string, weekCount: number): Promise<PayPeriodCard[]> {
    const { data } = await axios.get<PayPeriodCard[]>(
      `/TimeEntry/GetPayPeriodCardByKey/${payPeriodDate}/${encodeURIComponent(employeeNumber)}/${weekCount}`,
    )
    return data ?? []
  },

  async addPayPeriodCard(card: PayPeriodCard): Promise<boolean> {
    const { data } = await axios.post<boolean>('/TimeEntry/AddPayPeriodCard', card)
    return data === true
  },

  async updatePayPeriodCard(card: PayPeriodCard): Promise<boolean> {
    const { data } = await axios.put<boolean>('/TimeEntry/UpdatePayPeriodCard', card)
    return data === true
  },
}

export default dataService
